import * as Sentry from '@sentry/react';
import { extraErrorDataIntegration } from '@sentry/react';
import { currentEnvironment, environmentName, EnvironmentNames, isOnPremise } from './environment';
import SessionExpiredError from './SessionExpiredError';
import ErrorIgnoreSentry from './ErrorIgnoreSentry';

const isSentryActive = (): boolean => !isOnPremise() && currentEnvironment()?.name !== EnvironmentNames.DEVELOPMENT;

function initialize(): void {
  if (isSentryActive()) {
    Sentry.init({
      dsn: 'https://c3deeef7794f4d1b8196016977a8be09@sentry.io/1808155',
      integrations: [extraErrorDataIntegration(), Sentry.browserTracingIntegration()],
      environment: environmentName(),
      // Defines how many transactions to capture for performance monitoring
      // Currently numbers are low for testing purposes
      tracesSampleRate: 0.05,
      tracePropagationTargets: ['https://.*.salv.com/.*'],
      beforeSend(event, hint) {
        if (
          hint.originalException instanceof ErrorIgnoreSentry ||
          hint.originalException instanceof SessionExpiredError
        ) {
          return null;
        }

        // Why? Because query params can include PII
        if (event.request?.url) {
          const strippedUrl = new URL(event.request.url);
          strippedUrl.search = ''; // Clear the query parameters
          event.request.url = strippedUrl.toString();
        }

        if (event.request?.headers?.['Referer']) {
          const strippedUrl = new URL(event.request.headers['Referer']);
          strippedUrl.search = ''; // Clear the query parameters
          event.request.headers['Referer'] = strippedUrl.toString();
        }

        return event;
      },
      beforeBreadcrumb(breadcrumb) {
        if (breadcrumb.category === 'navigation' && breadcrumb.data) {
          breadcrumb.data.from = breadcrumb.data.from.split('?')[0];
          breadcrumb.data.to = breadcrumb.data.to.split('?')[0];
        }

        if (breadcrumb.category === 'fetch' && breadcrumb.message) {
          const [method, url, status] = breadcrumb.message.split(' ');
          breadcrumb.message = `${method} ${url.split('?')[0]} ${status}`;
        }

        return breadcrumb;
      },
    });
  }
}

const breadcrumb = (message: string) => {
  if (isSentryActive()) {
    Sentry.addBreadcrumb({
      category: 'manual',
      message: message,
      level: 'info',
    });
  }
};

const error = (error: unknown) => {
  if (isSentryActive()) {
    Sentry.captureException(error);
  }
};

function setUser(userId: number | null, organisationId: number | null): void {
  if (isSentryActive()) {
    Sentry.setContext('user', {
      userId: userId,
      organisationId: organisationId,
    });
  }
}

export default { initialize, setUser, error, breadcrumb };
